import { api } from "@/lib/api/client"
import type { BaseResponse, IdOnly } from "@/types/api"

import type {
  CreateQCLogPayload,
  ListLotsParams,
  LotDetail,
  LotListItem,
  LotQCLog,
  PatchLotPayload,
  PatchQCLogPayload,
  TopUpLotPayload,
} from "./types"

const BASE = "/exporter/lots"

export async function listLots(
  params: ListLotsParams = {},
): Promise<LotListItem[]> {
  const { data } = await api.get<BaseResponse<LotListItem[]>>(BASE, {
    params,
  })
  if (!data.success) {
    throw new Error(data.message ?? "Could not load lots")
  }
  return data.result ?? []
}

export interface GetLotOptions {
  // When true, the lot's `sources[].load` embed grows to include
  // `deliveryForm` and `order.cooperative`. Off by default to keep the
  // base payload small for callers that just need bag counts.
  includeLoads?: boolean
}

export async function getLot(
  lotId: string | number,
  opts: GetLotOptions = {},
): Promise<LotDetail> {
  const { data } = await api.get<BaseResponse<LotDetail>>(`${BASE}/${lotId}`, {
    params: opts.includeLoads ? { includeLoads: true } : undefined,
  })
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not load lot")
  }
  return data.result
}

export async function patchLot(
  lotId: string | number,
  payload: PatchLotPayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/${lotId}`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not update lot")
  }
  return data.result
}

export async function topUpLot(
  lotId: string | number,
  payload: TopUpLotPayload,
): Promise<LotDetail> {
  const { data } = await api.post<BaseResponse<LotDetail>>(
    `${BASE}/${lotId}/top-up`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not top up lot")
  }
  return data.result
}

export async function createQCLog(
  lotId: string | number,
  payload: CreateQCLogPayload,
): Promise<LotQCLog> {
  const { data } = await api.post<BaseResponse<LotQCLog>>(
    `${BASE}/${lotId}/qc-logs`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not create QC log")
  }
  return data.result
}

export async function patchQCLog(
  lotId: string | number,
  logId: string | number,
  payload: PatchQCLogPayload,
): Promise<IdOnly> {
  const { data } = await api.patch<BaseResponse<IdOnly>>(
    `${BASE}/${lotId}/qc-logs/${logId}`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not update QC log")
  }
  return data.result
}

export async function deleteQCLog(
  lotId: string | number,
  logId: string | number,
): Promise<IdOnly> {
  const { data } = await api.delete<BaseResponse<IdOnly>>(
    `${BASE}/${lotId}/qc-logs/${logId}`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not delete QC log")
  }
  return data.result
}
